import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiPlus,
  FiSettings,
  FiXCircle,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";

import {
  config,
} from "../../../../../../utils/apiUrl";

import EmployeePageHeader
  from "../common/EmployeePageHeader";

import EmployeeModuleTabs
  from "../common/EmployeeModuleTabs";

import EmployeeSelector
  from "../common/EmployeeSelector";

import EmployeeConfirmModal
  from "../common/EmployeeConfirmModal";

import EmployeeLeaveTable
  from "./EmployeeLeaveTable";

import AddLeaveModal
  from "./AddLeaveModal";

import LeaveDetailsDrawer
  from "./LeaveDetailsDrawer";

import LeaveTypeModal
  from "./LeaveTypeModal";

import {
  getEmployeeApiError,
} from "../utils/employee.utils";

import "./EmployeeLeave.css";


const INITIAL_FILTERS = {
  employeeId: "",
  leaveTypeId: "",
  status: "",
  year:
    new Date()
      .getFullYear(),
  search: "",
};


const INITIAL_PAGINATION = {
  page: 1,
  limit: 10,
  totalRecords: 0,
  totalPages: 1,
};


const INITIAL_SUMMARY = {
  totalLeaves: 0,
  pendingLeaves: 0,
  approvedLeaves: 0,
  rejectedLeaves: 0,
  cancelledLeaves: 0,
  totalApprovedDays: 0,
};


const EmployeeLeave = () => {

  const api =
    useMemo(
      () =>
        new API(),
      []
    );


  const [
    leaves,
    setLeaves,
  ] =
    useState([]);


  const [
    employees,
    setEmployees,
  ] =
    useState([]);


  const [
    leaveTypes,
    setLeaveTypes,
  ] =
    useState([]);


  const [
    summary,
    setSummary,
  ] =
    useState({
      ...INITIAL_SUMMARY,
    });


  const [
    filters,
    setFilters,
  ] =
    useState({
      ...INITIAL_FILTERS,
    });


  const [
    appliedFilters,
    setAppliedFilters,
  ] =
    useState({
      ...INITIAL_FILTERS,
    });


  const [
    pagination,
    setPagination,
  ] =
    useState({
      ...INITIAL_PAGINATION,
    });


  const [
    loading,
    setLoading,
  ] =
    useState(false);


  const [
    employeeLoading,
    setEmployeeLoading,
  ] =
    useState(false);


  const [
    leaveTypeLoading,
    setLeaveTypeLoading,
  ] =
    useState(false);


  const [
    actionLoading,
    setActionLoading,
  ] =
    useState(false);


  const [
    error,
    setError,
  ] =
    useState("");


  const [
    selectedLeave,
    setSelectedLeave,
  ] =
    useState(null);


  const [
    addLeaveOpen,
    setAddLeaveOpen,
  ] =
    useState(false);


  const [
    detailsOpen,
    setDetailsOpen,
  ] =
    useState(false);


  const [
    leaveTypeOpen,
    setLeaveTypeOpen,
  ] =
    useState(false);


  const [
    approveOpen,
    setApproveOpen,
  ] =
    useState(false);


  const [
    rejectOpen,
    setRejectOpen,
  ] =
    useState(false);


  const [
    cancelOpen,
    setCancelOpen,
  ] =
    useState(false);


  /*
  |--------------------------------------------------------------------------
  | LOAD EMPLOYEES
  |--------------------------------------------------------------------------
  */

  const loadEmployees =
    useCallback(
      async () => {

        try {

          setEmployeeLoading(
            true
          );


          const response =
            await api.post(
              config.getEmployees,
              {
                page: 1,
                limit: 500,
              }
            );


          if (
            response?.status !==
            "success"
          ) {

            throw new Error(
              response?.message ||
              "Unable to load employees."
            );

          }


          setEmployees(
            Array.isArray(
              response?.data
            )
              ?
              response.data
              :
              []
          );

        } catch (
          requestError
        ) {

          console.error(
            "Leave employee list error:",
            requestError
          );


          setEmployees(
            []
          );

        } finally {

          setEmployeeLoading(
            false
          );

        }

      },
      [
        api,
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | LEAVE TYPES
  |--------------------------------------------------------------------------
  */

  const loadLeaveTypes =
    useCallback(
      async () => {

        try {

          setLeaveTypeLoading(
            true
          );


          const response =
            await api.get(
              config.getEmployeeLeaveTypes
            );


          if (
            response?.status !==
            "success"
          ) {

            throw new Error(
              response?.message ||
              "Unable to load leave types."
            );

          }


          const data =
            Array.isArray(
              response?.data
            )
              ?
              response.data
              :
              response
                ?.data
                ?.leaveTypes ||
              [];


          setLeaveTypes(
            data
          );

        } catch (
          requestError
        ) {

          console.error(
            "Leave type error:",
            requestError
          );


          setLeaveTypes(
            []
          );

        } finally {

          setLeaveTypeLoading(
            false
          );

        }

      },
      [
        api,
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | LEAVE LIST
  |--------------------------------------------------------------------------
  */

  const loadLeaves =
    useCallback(
      async () => {

        try {

          setLoading(
            true
          );


          setError(
            ""
          );


          const response =
            await api.post(
              config.getEmployeeLeaves,
              {
                page:
                  pagination.page,

                limit:
                  pagination.limit,

                employeeId:
                  appliedFilters.employeeId,

                leaveTypeId:
                  appliedFilters.leaveTypeId,

                status:
                  appliedFilters.status,

                year:
                  Number(
                    appliedFilters.year
                  ),

                search:
                  appliedFilters.search,
              }
            );


          if (
            response?.status !==
            "success"
          ) {

            throw new Error(
              response?.message ||
              "Unable to load employee leaves."
            );

          }


          const data =
            Array.isArray(
              response?.data
            )
              ?
              response.data
              :
              [];


          setLeaves(
            data
          );


          setPagination(
            (
              previous
            ) => ({
              ...previous,

              totalRecords:
                Number(
                  response
                    ?.pagination
                    ?.totalRecords ??
                  data.length
                ),

              totalPages:
                Math.max(
                  Number(
                    response
                      ?.pagination
                      ?.totalPages
                  ) ||
                  1,
                  1
                ),
            })
          );


          if (
            response?.summary
          ) {

            setSummary({
              ...INITIAL_SUMMARY,
              ...response.summary,
            });

          }

        } catch (
          requestError
        ) {

          console.error(
            "Leave list error:",
            requestError
          );


          setLeaves(
            []
          );


          setError(
            getEmployeeApiError(
              requestError,
              "Unable to load employee leaves."
            )
          );

        } finally {

          setLoading(
            false
          );

        }

      },
      [
        api,
        appliedFilters,
        pagination.page,
        pagination.limit,
      ]
    );


  useEffect(
    () => {

      loadEmployees();
      loadLeaveTypes();

    },
    [
      loadEmployees,
      loadLeaveTypes,
    ]
  );


  useEffect(
    () => {

      loadLeaves();

    },
    [
      loadLeaves,
    ]
  );


  /*
  |--------------------------------------------------------------------------
  | FILTERS
  |--------------------------------------------------------------------------
  */

  const handleSearch =
    () => {

      setPagination(
        (
          previous
        ) => ({
          ...previous,
          page: 1,
        })
      );


      setAppliedFilters({
        ...filters,

        search:
          String(
            filters.search ||
            ""
          ).trim(),
      });

    };


  const handleReset =
    () => {

      setFilters({
        ...INITIAL_FILTERS,
      });


      setAppliedFilters({
        ...INITIAL_FILTERS,
      });


      setPagination(
        (
          previous
        ) => ({
          ...previous,
          page: 1,
        })
      );

    };


  /*
  |--------------------------------------------------------------------------
  | SUCCESS
  |--------------------------------------------------------------------------
  */

  const handleSuccess =
    async () => {

      setAddLeaveOpen(
        false
      );

      setDetailsOpen(
        false
      );

      setApproveOpen(
        false
      );

      setRejectOpen(
        false
      );

      setCancelOpen(
        false
      );

      setSelectedLeave(
        null
      );


      await loadLeaves();

    };


  /*
  |--------------------------------------------------------------------------
  | APPROVE
  |--------------------------------------------------------------------------
  */

  const approveLeave =
    async () => {

      if (
        !selectedLeave?._id
      ) {

        return;

      }


      try {

        setActionLoading(
          true
        );


        const response =
          await api.patch(
            config.approveEmployeeLeave(
              selectedLeave._id
            ),
            {}
          );


        if (
          response?.status !==
          "success"
        ) {

          throw new Error(
            response?.message ||
            "Unable to approve leave."
          );

        }


        await handleSuccess();

      } catch (
        requestError
      ) {

        setError(
          getEmployeeApiError(
            requestError,
            "Unable to approve leave."
          )
        );

      } finally {

        setActionLoading(
          false
        );

      }

    };


  /*
  |--------------------------------------------------------------------------
  | REJECT
  |--------------------------------------------------------------------------
  */

  const rejectLeave =
    async (
      reason
    ) => {

      if (
        !selectedLeave?._id
      ) {

        return;

      }


      try {

        setActionLoading(
          true
        );


        const response =
          await api.patch(
            config.rejectEmployeeLeave(
              selectedLeave._id
            ),
            {
              reason,
            }
          );


        if (
          response?.status !==
          "success"
        ) {

          throw new Error(
            response?.message ||
            "Unable to reject leave."
          );

        }


        await handleSuccess();

      } catch (
        requestError
      ) {

        setError(
          getEmployeeApiError(
            requestError,
            "Unable to reject leave."
          )
        );

      } finally {

        setActionLoading(
          false
        );

      }

    };


  /*
  |--------------------------------------------------------------------------
  | CANCEL
  |--------------------------------------------------------------------------
  */

  const cancelLeave =
    async (
      reason
    ) => {

      if (
        !selectedLeave?._id
      ) {

        return;

      }


      try {

        setActionLoading(
          true
        );


        const response =
          await api.patch(
            config.cancelEmployeeLeave(
              selectedLeave._id
            ),
            {
              reason,
            }
          );


        if (
          response?.status !==
          "success"
        ) {

          throw new Error(
            response?.message ||
            "Unable to cancel leave."
          );

        }


        await handleSuccess();

      } catch (
        requestError
      ) {

        setError(
          getEmployeeApiError(
            requestError,
            "Unable to cancel leave."
          )
        );

      } finally {

        setActionLoading(
          false
        );

      }

    };


  const summaryCards = [

    {
      label:
        "Total Leaves",

      value:
        summary.totalLeaves,

      icon:
        FiCalendar,

      type:
        "blue",
    },

    {
      label:
        "Pending",

      value:
        summary.pendingLeaves,

      icon:
        FiClock,

      type:
        "orange",
    },

    {
      label:
        "Approved",

      value:
        summary.approvedLeaves,

      icon:
        FiCheckCircle,

      type:
        "green",
    },

    {
      label:
        "Rejected",

      value:
        summary.rejectedLeaves,

      icon:
        FiXCircle,

      type:
        "red",
    },

  ];


  return (

    <div className="employee-leave-page">

      <EmployeePageHeader
        title="Leave Management"
        description="Manage employee leave requests, approvals, balances and leave policies."
        actionLabel="Add Leave"
        actionIcon={
          <FiPlus />
        }
        onAction={
          () =>
            setAddLeaveOpen(
              true
            )
        }
        onRefresh={
          () =>
            Promise.all([
              loadLeaves(),
              loadLeaveTypes(),
            ])
        }
        loading={
          loading
        }
      />


      <EmployeeModuleTabs />


      <div className="employee-leave-toolbar">

        <button
          type="button"
          onClick={
            () =>
              setLeaveTypeOpen(
                true
              )
          }
        >

          <FiSettings />

          Manage Leave Types

        </button>

      </div>


      <div className="employee-leave-summary-grid">

        {
          summaryCards.map(
            (
              card
            ) => {

              const Icon =
                card.icon;


              return (

                <article
                  key={
                    card.label
                  }
                  className={`employee-leave-summary-card employee-leave-summary-card--${card.type}`}
                >

                  <span>

                    <Icon />

                  </span>


                  <div>

                    <small>
                      {card.label}
                    </small>

                    <strong>
                      {
                        Number(
                          card.value ||
                          0
                        )
                      }
                    </strong>

                  </div>

                </article>

              );

            }
          )
        }

      </div>


      <section className="employee-leave-filter-card">

        <div className="employee-leave-filter-grid">

          <EmployeeSelector
            employees={
              employees
            }
            value={
              filters.employeeId
            }
            onChange={
              (
                employeeId
              ) =>
                setFilters(
                  (
                    previous
                  ) => ({
                    ...previous,
                    employeeId,
                  })
                )
            }
            label="Employee"
            placeholder="All Employees"
            disabled={
              employeeLoading
            }
          />


          <div className="employee-leave-filter-field">

            <label>
              Leave Type
            </label>

            <select
              value={
                filters.leaveTypeId
              }
              onChange={
                (
                  event
                ) =>
                  setFilters(
                    (
                      previous
                    ) => ({
                      ...previous,

                      leaveTypeId:
                        event
                          .target
                          .value,
                    })
                  )
              }
              disabled={
                leaveTypeLoading
              }
            >

              <option value="">
                All Leave Types
              </option>

              {
                leaveTypes.map(
                  (
                    type
                  ) => (

                    <option
                      key={
                        type._id
                      }
                      value={
                        type._id
                      }
                    >

                      {
                        type.name
                      }

                    </option>

                  )
                )
              }

            </select>

          </div>


          <div className="employee-leave-filter-field">

            <label>
              Status
            </label>

            <select
              value={
                filters.status
              }
              onChange={
                (
                  event
                ) =>
                  setFilters(
                    (
                      previous
                    ) => ({
                      ...previous,

                      status:
                        event
                          .target
                          .value,
                    })
                  )
              }
            >

              <option value="">
                All Status
              </option>

              <option value="PENDING">
                Pending
              </option>

              <option value="APPROVED">
                Approved
              </option>

              <option value="REJECTED">
                Rejected
              </option>

              <option value="CANCELLED">
                Cancelled
              </option>

            </select>

          </div>


          <div className="employee-leave-filter-field">

            <label>
              Year
            </label>

            <select
              value={
                filters.year
              }
              onChange={
                (
                  event
                ) =>
                  setFilters(
                    (
                      previous
                    ) => ({
                      ...previous,

                      year:
                        Number(
                          event
                            .target
                            .value
                        ),
                    })
                  )
              }
            >

              {
                Array.from(
                  {
                    length: 7,
                  },
                  (
                    _,
                    index
                  ) =>
                    new Date()
                      .getFullYear() -
                    3 +
                    index
                )
                  .map(
                    (
                      year
                    ) => (

                      <option
                        key={
                          year
                        }
                        value={
                          year
                        }
                      >
                        {year}
                      </option>

                    )
                  )
              }

            </select>

          </div>


          <div className="employee-leave-filter-field">

            <label>
              Search
            </label>

            <input
              type="text"
              value={
                filters.search
              }
              onChange={
                (
                  event
                ) =>
                  setFilters(
                    (
                      previous
                    ) => ({
                      ...previous,

                      search:
                        event
                          .target
                          .value,
                    })
                  )
              }
              onKeyDown={
                (
                  event
                ) => {

                  if (
                    event.key ===
                    "Enter"
                  ) {

                    handleSearch();

                  }

                }
              }
              placeholder="Employee name/code"
            />

          </div>


          <div className="employee-leave-filter-actions">

            <button
              type="button"
              className="employee-leave-apply"
              onClick={
                handleSearch
              }
            >
              Apply
            </button>


            <button
              type="button"
              className="employee-leave-reset"
              onClick={
                handleReset
              }
            >
              Reset
            </button>

          </div>

        </div>

      </section>


      {
        error
          ?
          (
            <div className="employee-leave-error">
              {error}
            </div>
          )
          :
          null
      }


      <EmployeeLeaveTable
        leaves={
          leaves
        }
        loading={
          loading
        }
        page={
          pagination.page
        }
        limit={
          pagination.limit
        }
        totalRecords={
          pagination.totalRecords
        }
        totalPages={
          pagination.totalPages
        }
        onPageChange={
          (
            page
          ) =>
            setPagination(
              (
                previous
              ) => ({
                ...previous,
                page,
              })
            )
        }
        onLimitChange={
          (
            limit
          ) =>
            setPagination(
              (
                previous
              ) => ({
                ...previous,
                page: 1,
                limit,
              })
            )
        }
        onView={
          (
            leave
          ) => {

            setSelectedLeave(
              leave
            );

            setDetailsOpen(
              true
            );

          }
        }
        onApprove={
          (
            leave
          ) => {

            setSelectedLeave(
              leave
            );

            setApproveOpen(
              true
            );

          }
        }
        onReject={
          (
            leave
          ) => {

            setSelectedLeave(
              leave
            );

            setRejectOpen(
              true
            );

          }
        }
        onCancel={
          (
            leave
          ) => {

            setSelectedLeave(
              leave
            );

            setCancelOpen(
              true
            );

          }
        }
      />


      <AddLeaveModal
        open={
          addLeaveOpen
        }
        employees={
          employees
        }
        leaveTypes={
          leaveTypes
        }
        onClose={
          () =>
            setAddLeaveOpen(
              false
            )
        }
        onSuccess={
          handleSuccess
        }
      />


      <LeaveDetailsDrawer
        open={
          detailsOpen
        }
        leave={
          selectedLeave
        }
        onClose={
          () => {

            setDetailsOpen(
              false
            );

            setSelectedLeave(
              null
            );

          }
        }
        onApprove={
          (
            leave
          ) => {

            setDetailsOpen(
              false
            );

            setSelectedLeave(
              leave
            );

            setApproveOpen(
              true
            );

          }
        }
        onReject={
          (
            leave
          ) => {

            setDetailsOpen(
              false
            );

            setSelectedLeave(
              leave
            );

            setRejectOpen(
              true
            );

          }
        }
        onCancel={
          (
            leave
          ) => {

            setDetailsOpen(
              false
            );

            setSelectedLeave(
              leave
            );

            setCancelOpen(
              true
            );

          }
        }
      />


      <LeaveTypeModal
        open={
          leaveTypeOpen
        }
        leaveTypes={
          leaveTypes
        }
        onClose={
          () =>
            setLeaveTypeOpen(
              false
            )
        }
        onSuccess={
          async () => {

            await loadLeaveTypes();

          }
        }
      />


      <EmployeeConfirmModal
        open={
          approveOpen
        }
        title="Approve Leave"
        description="Approve this employee leave request?"
        confirmLabel="Approve Leave"
        loading={
          actionLoading
        }
        onClose={
          () => {

            setApproveOpen(
              false
            );

            setSelectedLeave(
              null
            );

          }
        }
        onConfirm={
          approveLeave
        }
      />


      <EmployeeConfirmModal
        open={
          rejectOpen
        }
        title="Reject Leave"
        description="Reject this leave request and provide a reason."
        confirmLabel="Reject Leave"
        requireReason
        reasonLabel="Rejection Reason"
        reasonPlaceholder="Enter rejection reason"
        danger
        loading={
          actionLoading
        }
        onClose={
          () => {

            setRejectOpen(
              false
            );

            setSelectedLeave(
              null
            );

          }
        }
        onConfirm={
          rejectLeave
        }
      />


      <EmployeeConfirmModal
        open={
          cancelOpen
        }
        title="Cancel Leave"
        description="Cancel this leave request?"
        confirmLabel="Cancel Leave"
        requireReason
        reasonLabel="Cancellation Reason"
        reasonPlaceholder="Enter cancellation reason"
        danger
        loading={
          actionLoading
        }
        onClose={
          () => {

            setCancelOpen(
              false
            );

            setSelectedLeave(
              null
            );

          }
        }
        onConfirm={
          cancelLeave
        }
      />

    </div>

  );

};


export default EmployeeLeave;